// Marketing-side integration list. Sourced from a snapshot of the live
// REGISTRY in @memkernel/integrations so the homepage and /integrations
// page show exactly what we ship — no separate curated list to drift.
//
// The snapshot lives in `integrations.generated.ts`; rebuild it after
// editing any service's metadata with:
//
//   bun scripts/snapshot_marketing_meta.ts
//
// We snapshot (rather than import REGISTRY directly) because REGISTRY
// pulls every tool handler + zod schema into the bundle — ~6.7 MB.
// Marketing only needs 4 fields per row.

import { INTEGRATIONS_META } from "./integrations.generated";

export interface Integration {
  /** Same slug the runtime uses; serves the logo at `/logos/v3/<slug>.svg`. */
  slug: string;
  /** Display name. */
  name: string;
  /** Runtime category — one of the strings the kit categorizes services under. */
  category: string;
  /** One-liner shown on cards. */
  summary: string;
}

/**
 * Live category labels. Keys are the raw `integration.category` values
 * the kit uses ("ai-llm", "issue-tracking", …) — we keep them stable so
 * URLs like `/integrations?category=crm` line up with the runtime.
 */
export const CATEGORY_LABELS: Record<string, string> = {
  "ai-llm": "AI & LLMs",
  analytics: "Analytics",
  billing: "Billing & Payments",
  browser: "Browsers & Scraping",
  calendar: "Calendar",
  code: "Code & DevOps",
  crm: "CRM & Sales",
  data: "Data & Databases",
  design: "Design",
  "docs-knowledge": "Docs & Knowledge",
  ecommerce: "E-commerce",
  email: "Email",
  files: "Storage & Files",
  forms: "Forms",
  "issue-tracking": "Issue Tracking",
  messaging: "Messaging",
  monitoring: "Monitoring",
  "no-code": "No-code",
  scheduling: "Scheduling",
  search: "Search",
  social: "Social",
  support: "Support",
  video: "Video",
};

/**
 * Materialized snapshot of the live registry's marketing metadata.
 * Frozen so accidental mutation in a render trips a warning.
 */
export const INTEGRATIONS: readonly Integration[] = Object.freeze(
  INTEGRATIONS_META.map((r) => ({ ...r })),
);

/**
 * Categories present in the live registry, in a stable display order.
 * Defined in code (not derived from data) so the order is intentional
 * even as new integrations land.
 */
const PRESENTATION_ORDER: readonly string[] = [
  "ai-llm",
  "code",
  "crm",
  "ecommerce",
  "billing",
  "messaging",
  "email",
  "calendar",
  "scheduling",
  "files",
  "data",
  "support",
  "issue-tracking",
  "docs-knowledge",
  "design",
  "monitoring",
  "analytics",
  "search",
  "browser",
  "social",
  "video",
  "forms",
  "no-code",
];

export const CATEGORY_ORDER: string[] = (() => {
  const present = new Set<string>(INTEGRATIONS.map((i) => i.category));
  const ordered: string[] = PRESENTATION_ORDER.filter((c) => present.has(c));
  // Append any registry categories not in the presentation order so we
  // never silently drop a new one.
  for (const c of present) {
    if (!ordered.includes(c)) ordered.push(c);
  }
  return ordered;
})();

/** Total integration count — used by marketing copy ("100+ integrations"). */
export const INTEGRATION_COUNT = INTEGRATIONS.length;
